/*
 * label-maker/presets.js — starting designs.
 *
 * A preset is a list of elements in RELATIVE geometry (0..1 of the label's safe
 * area), which is what lets the same design survive being moved from a 38×21 mm
 * address label to a 105×74 mm jar label without anything being re-dragged.
 * Type size is relative too (`sizeRel`, a fraction of label height) for the same
 * reason.
 *
 * The set is chosen from what the RU market measurably searches for — exercise
 * books and kindergarten name tags, jars and preserves, price tags, inventory
 * numbers, address labels — not from what looks good in a gallery. Each one is
 * a real job someone sat down to do.
 *
 * `{{value}}` is the default merge field, so every preset works immediately
 * with a pasted list and needs mapping only for the multi-field ones.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelPresets = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Keys are stable ids; the human-readable name comes from the page's
     * translation table, never from here — this file must not carry copy. */
    var PRESETS = [
        {
            id: 'plain',
            best: ['any'],
            elements: [
                { type: 'text', name: 'text', text: '{{value}}', x: 0, y: 0, w: 1, h: 1,
                  sizeRel: 0.38, align: 'center', valign: 'middle', font: 'noto', bold: true },
            ],
        },
        {
            id: 'notebook',            // имя на тетрадь
            best: ['div-24', 'div-21', 'avery-l7160'],
            elements: [
                { type: 'text', name: 'name', text: '{{value}}', x: 0, y: 0.05, w: 1, h: 0.52,
                  sizeRel: 0.30, align: 'center', valign: 'middle', font: 'montserrat', bold: true },
                { type: 'shape', shape: 'line', x: 0.15, y: 0.60, w: 0.7, h: 0.02,
                  stroke: '#2f6fd0', strokeWidth: 0.35 },
                { type: 'text', name: 'sub', text: '{{класс}}', x: 0, y: 0.64, w: 1, h: 0.3,
                  sizeRel: 0.17, align: 'center', valign: 'middle', font: 'noto', color: '#4a5568' },
            ],
        },
        {
            id: 'kindergarten',        // бирка в шкафчик / на одежду
            best: ['div-40', 'div-24', 'avery-l7651'],
            elements: [
                { type: 'shape', shape: 'rect', x: 0, y: 0, w: 1, h: 1, bleed: true, fill: '#fdf3e3' },
                { type: 'text', name: 'name', text: '{{value}}', x: 0.04, y: 0.08, w: 0.92, h: 0.84,
                  sizeRel: 0.34, align: 'center', valign: 'middle', font: 'comfortaa', bold: true,
                  color: '#8a4b1f' },
            ],
        },
        {
            id: 'jar',                 // банка со специей / крупой
            best: ['round-40', 'round-60', 'div-12'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0.18, w: 1, h: 0.4,
                  sizeRel: 0.22, align: 'center', valign: 'middle', font: 'yeseva', color: '#2c2c2c' },
                { type: 'shape', shape: 'line', x: 0.25, y: 0.60, w: 0.5, h: 0.02,
                  stroke: '#9a8460', strokeWidth: 0.3 },
                { type: 'text', name: 'sub', text: '{{подпись}}', x: 0, y: 0.64, w: 1, h: 0.22,
                  sizeRel: 0.12, align: 'center', valign: 'middle', font: 'noto', color: '#7a6a52' },
            ],
        },
        {
            id: 'preserve',            // заготовка / заморозка: что и когда
            best: ['div-12', 'div-14', 'avery-l7163'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0.06, w: 1, h: 0.44,
                  sizeRel: 0.26, align: 'center', valign: 'middle', font: 'noto', bold: true },
                { type: 'text', name: 'date', text: '{{дата}}', x: 0, y: 0.54, w: 1, h: 0.3,
                  sizeRel: 0.18, align: 'center', valign: 'middle', font: 'noto', color: '#555555' },
            ],
        },
        {
            id: 'address',             // адресная наклейка на конверт/посылку
            best: ['avery-l7163', 'div-14', 'avery-l7160'],
            elements: [
                { type: 'text', name: 'to', text: '{{value}}', x: 0, y: 0.02, w: 1, h: 0.34,
                  sizeRel: 0.20, align: 'left', valign: 'top', font: 'noto', bold: true },
                { type: 'text', name: 'addr', text: '{{адрес}}', x: 0, y: 0.36, w: 1, h: 0.44,
                  sizeRel: 0.15, align: 'left', valign: 'top', font: 'noto' },
                { type: 'text', name: 'zip', text: '{{индекс}}', x: 0, y: 0.80, w: 1, h: 0.18,
                  sizeRel: 0.16, align: 'left', valign: 'bottom', font: 'noto', letterSpacing: 0.4 },
            ],
        },
        {
            id: 'price',               // ценник
            best: ['div-24', 'div-21', 'div-16'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0.04, w: 1, h: 0.36,
                  sizeRel: 0.18, align: 'center', valign: 'middle', font: 'noto' },
                { type: 'text', name: 'price', text: '{{цена}}', x: 0, y: 0.40, w: 1, h: 0.56,
                  sizeRel: 0.42, align: 'center', valign: 'middle', font: 'oswald', bold: true },
            ],
        },
        {
            id: 'inventory',           // инвентарный номер со штрихкодом
            best: ['div-24', 'avery-l7651', 'div-40'],
            elements: [
                // showText off: the caption below is the human-readable line,
                // and bwip's own would print the same value twice.
                { type: 'barcode', name: 'code', symbology: 'code128', text: '{{value}}',
                  x: 0, y: 0, w: 1, h: 0.66, showText: false },
                { type: 'text', name: 'label', text: '{{value}}', x: 0, y: 0.68, w: 1, h: 0.3,
                  sizeRel: 0.20, align: 'center', valign: 'middle', font: 'noto',
                  letterSpacing: 0.2 },
            ],
        },
        {
            /* Этикетка для Ozon и Wildberries. Поля и их предельные длины взяты
               с генератора МойСклада (замер 2026-08-20): название ≤48, штрихкод
               ≤30, артикул ≤22, цвет ≤24, размер ≤18. Код — Code-128, как
               требуют обе площадки. Спрос: «этикетка для озон» 3 309/мес +
               «для вайлдберриз» 1 490. У МойСклада бесплатно 18 штук за раз и
               только A4 — у нас столько же на листе, но без потолка. */
            id: 'marketplace',
            best: ['mk-58x40', 'div-18', 'div-21'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0.02, w: 1, h: 0.22,
                  sizeRel: 0.13, align: 'center', valign: 'middle', font: 'noto', bold: true },
                { type: 'barcode', name: 'code', symbology: 'code128', text: '{{штрихкод}}',
                  x: 0.03, y: 0.25, w: 0.94, h: 0.44, showText: false },
                { type: 'text', name: 'sku', text: '{{артикул}}', x: 0, y: 0.70, w: 1, h: 0.16,
                  sizeRel: 0.12, align: 'center', valign: 'middle', font: 'noto',
                  letterSpacing: 0.2 },
                { type: 'text', name: 'attrs', text: '{{цвет}} · {{размер}}', x: 0, y: 0.85, w: 1, h: 0.14,
                  sizeRel: 0.10, align: 'center', valign: 'middle', font: 'noto' },
            ],
        },
        {
            /* Визитка 90×50 — российский стандарт. «Визитки распечатать» —
               2 959/мес, а геометрии под них у нас не было вовсе. */
            id: 'card',
            best: ['card-90x50', 'div-10', 'avery-5395'],
            elements: [
                { type: 'text', name: 'name', text: '{{value}}', x: 0.08, y: 0.16, w: 0.84, h: 0.26,
                  sizeRel: 0.20, align: 'left', valign: 'middle', font: 'montserrat', bold: true },
                { type: 'text', name: 'role', text: '{{должность}}', x: 0.08, y: 0.42, w: 0.84, h: 0.18,
                  sizeRel: 0.13, align: 'left', valign: 'middle', font: 'noto' },
                { type: 'text', name: 'contact', text: '{{телефон}}', x: 0.08, y: 0.66, w: 0.84, h: 0.18,
                  sizeRel: 0.13, align: 'left', valign: 'middle', font: 'noto' },
            ],
        },
        {
            id: 'qr',                  // QR + подпись
            best: ['div-24', 'div-12', 'round-40'],
            elements: [
                { type: 'barcode', name: 'qr', symbology: 'qrcode', text: '{{value}}',
                  x: 0.28, y: 0, w: 0.44, h: 0.72, showText: false },
                { type: 'text', name: 'caption', text: '{{подпись}}', x: 0, y: 0.74, w: 1, h: 0.24,
                  sizeRel: 0.16, align: 'center', valign: 'middle', font: 'noto' },
            ],
        },
        {
            id: 'product',             // товарный штрихкод EAN-13
            best: ['div-21', 'avery-l7160', 'div-24'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0.02, w: 1, h: 0.26,
                  sizeRel: 0.17, align: 'center', valign: 'middle', font: 'noto', bold: true },
                { type: 'barcode', name: 'ean', symbology: 'ean13', text: '{{код}}',
                  x: 0.06, y: 0.30, w: 0.88, h: 0.66, showText: true },
            ],
        },
        {
            id: 'gift',                // подарочная бирка
            best: ['div-12', 'round-60', 'avery-l7165'],
            elements: [
                { type: 'shape', shape: 'rect', x: 0.02, y: 0.04, w: 0.96, h: 0.92,
                  stroke: '#c9a227', strokeWidth: 0.4 },
                { type: 'text', name: 'to', text: '{{value}}', x: 0.06, y: 0.16, w: 0.88, h: 0.4,
                  sizeRel: 0.2, align: 'center', valign: 'middle', font: 'marck', color: '#7a5c12' },
                { type: 'text', name: 'from', text: '{{от кого}}', x: 0.06, y: 0.58, w: 0.88, h: 0.28,
                  sizeRel: 0.13, align: 'center', valign: 'middle', font: 'noto', color: '#8a7440' },
            ],
        },
        {
            id: 'folder',              // корешок папки-регистратора
            best: ['folder-190x38', 'folder-190x25'],
            elements: [
                { type: 'text', name: 'title', text: '{{value}}', x: 0, y: 0, w: 0.78, h: 1,
                  sizeRel: 0.42, align: 'left', valign: 'middle', font: 'oswald', bold: true },
                { type: 'text', name: 'code', text: '{{номер}}', x: 0.80, y: 0, w: 0.2, h: 1,
                  sizeRel: 0.34, align: 'right', valign: 'middle', font: 'noto', color: '#555555' },
            ],
        },
    ];

    function all() { return PRESETS.map(clone); }

    function byId(id) {
        for (var i = 0; i < PRESETS.length; i++) if (PRESETS[i].id === id) return clone(PRESETS[i]);
        return null;
    }

    /* Presets whose proportions suit this sheet, best first. A 190×38 folder
     * spine design on a 38×21 address label is technically renderable and
     * practically useless, so the gallery orders by fit rather than showing
     * everything in a fixed order. */
    function forFormat(fmt) {
        var ratio = fmt.w / fmt.h;
        return all().sort(function (a, b) {
            var ai = a.best.indexOf(fmt.id), bi = b.best.indexOf(fmt.id);
            if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
            return Math.abs(hintRatio(a) - ratio) - Math.abs(hintRatio(b) - ratio);
        });
    }

    /* A rough "what shape was this drawn for", from the formats it names. */
    function hintRatio(p) {
        var S = (typeof window !== 'undefined' && window.LabelSheets) || null;
        if (!S) return 2;
        for (var i = 0; i < p.best.length; i++) {
            var f = S.byId(p.best[i]);
            if (f) return f.w / f.h;
        }
        return 2;
    }

    function clone(o) { return JSON.parse(JSON.stringify(o)); }

    return { all: all, byId: byId, forFormat: forFormat };
});
